import { createFileRoute, Link, ErrorComponent, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { getPostBySlug } from "@/lib/blog.functions";
import BlogHeader from "@/components/blog/BlogHeader";

const postQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPostBySlug({ data: { slug } }),
  });

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(postQuery(params.slug));
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData, params }) => {
    const p = loaderData;
    if (!p) return { meta: [{ title: "Post — Backland" }] };
    const url = `https://www.travelbackland.com/blog/${params.slug}`;
    const fallbackDescription = `Read "${p.title}" on the Backland blog — stories, guides, and notes from our luxury nature resort near the Grand Canyon in Northern Arizona.`;
    const description = p.excerpt && p.excerpt.length >= 50 ? p.excerpt : fallbackDescription;
    return {
      meta: [
        { title: `${p.title} — Backland Blog` },
        { name: "description", content: description },
        { property: "og:title", content: p.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(p.cover_image_url ? [{ property: "og:image", content: p.cover_image_url }] : []),
        ...(p.cover_image_url ? [{ name: "twitter:image", content: p.cover_image_url }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.title,
            description,
            url,
            ...(p.cover_image_url ? { image: p.cover_image_url } : {}),
            ...(p.published_at ? { datePublished: p.published_at } : {}),
          }),
        },
      ],
    };
  },
  errorComponent: ErrorComponent,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center text-center px-6">
      <div>
        <h1 className="text-2xl font-serif">Post not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-sm underline">
          ← Back to blog
        </Link>
      </div>
    </div>
  ),
  component: BlogPostPage,
});

function formatDate(iso: string | null) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return "";
  }
}

function stripHeaderElements(html: string, coverUrl: string | null, excerpt: string | null): string {
  if (!html) return html;
  let out = html.trimStart();

  // Remove a leading <figure>...</figure> wrapping the cover image
  out = out.replace(/^<figure\b[^>]*>[\s\S]*?<\/figure>\s*/i, (match) => {
    if (coverUrl && match.includes(coverUrl)) return "";
    if (/<img\b/i.test(match) && !coverUrl) return "";
    return match;
  });

  // Remove a leading <img ...> if it matches the cover
  out = out.replace(/^<img\b[^>]*>\s*/i, (match) => {
    if (!coverUrl) return "";
    return match.includes(coverUrl) ? "" : match;
  });

  // Remove a leading <p>...</p> if its text matches the excerpt
  if (excerpt) {
    const normalize = (s: string) => s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim().toLowerCase();
    const target = normalize(excerpt);
    out = out.replace(/^<p\b[^>]*>[\s\S]*?<\/p>\s*/i, (match) => {
      const text = normalize(match);
      if (!text) return match;
      if (text === target || text.startsWith(target) || target.startsWith(text)) return "";
      return match;
    });
  }

  return out;
}

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data: post } = useSuspenseQuery(postQuery(slug));
  if (!post) return null;

  const cleanedHtml = stripHeaderElements(post.content_html, post.cover_image_url, post.excerpt);
  const safeHtml = sanitizeHtml(cleanedHtml, {
    allowedTags: [
      "p","br","hr","strong","em","b","i","u","s","sub","sup","mark","small",
      "h1","h2","h3","h4","h5","h6",
      "ul","ol","li","dl","dt","dd",
      "blockquote","pre","code","kbd","samp",
      "a","img","figure","figcaption","picture","source",
      "table","thead","tbody","tfoot","tr","th","td","caption",
      "div","span",
    ],
    allowedAttrs: ["href","title","alt","src","srcset","sizes","width","height","loading","class","id","target","rel","colspan","rowspan","style","type","start"],
    allowedUriRegexp: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <BlogHeader fallbackBack={{ to: "/blog", label: "All posts" }} />

      <div className="max-w-3xl mx-auto px-6 pb-10">
        <header className="mt-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            {formatDate(post.published_at)}
            {post.minutes_to_read ? ` · ${post.minutes_to_read} min read` : ""}
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-serif leading-tight">{post.title}</h1>
          {post.excerpt && (
            <p className="mt-4 text-lg text-muted-foreground">{post.excerpt}</p>
          )}
        </header>

        {post.cover_image_url && (
          <div className="mt-8 overflow-hidden rounded-lg bg-muted">
            <img src={post.cover_image_url} alt="" className="w-full h-auto" />
          </div>
        )}

        <article
          className="prose prose-invert mt-10 max-w-none prose-img:rounded-lg prose-headings:font-serif prose-headings:text-foreground prose-p:text-foreground/90 prose-li:text-foreground/90 prose-strong:text-foreground prose-a:text-foreground prose-a:underline"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />

        <div className="mt-16 border-t border-border pt-8">
          <Link to="/blog" className="text-sm underline">
            ← Back to all posts
          </Link>
        </div>
      </div>
    </div>
  );
}

